import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { seatsShape, passengerShape } from 'constants/propTypes';
import {
  getSeatNumber,
  getSeatStatus,
  setSeatAsAssigned,
  getSeatPassengerId,
  setSeatAsSelected,
  setSeatAsNonEditable
} from 'utils/seating/seat';
import { status, STATUS_ATTRIBUTE } from 'constants/seating';
import { getSeatElement, onSeatClick, resetSeatMap } from 'utils/seating/seatMap';
import { getSeatMapContainer, onSeatMapContainerLoad } from 'utils/seating/seatMapContainer';
import { setSeatAsBlocked } from 'utils/seating/seat/seat';

import { svgStyle, svgStyleER } from 'components/ui/SeatMapLegend/Seat.styles';

import { fonts } from 'themes/default';

const StyledSvgObject = styled('object')`
  width: 100%;
  max-height: 245px;
`;

const SeatMapErrorMarkUp = () => (
  <>
    <FormattedMessage id="seatmap.svgError" />
    <button type="submit" onClick={() => window.location.reload()}>
      Try again
    </button>
  </>
);

const SeatMapSvg = ({
  templateName,
  url,
  className,
  seats,
  currentCoach,
  selectedPassenger,
  selectedPassengers,
  onRemovePassengerSelection,
  onSelectNewPassenger,
  onSelectNewSeat,
  onSelectNewSeats,
  availableSeats,
  onShowUnavailableSeatReason,
  seatsData,
  prioritySeats,
  originalSeats,
  isERTrain
}) => {
  const onClickRef = useRef(null);

  const createOnClickHandler = useCallback(
    (seatMapSvg) =>
      onSeatClick(seatMapSvg, (seatElement) => {
        const seatPassengerId = getSeatPassengerId(seatElement);

        if (seatElement) {
          const seat = getSeatNumber(seatElement);
          const seatToUse = seat?.charAt(0) === '0' ? (seat?.charAt(1) === '0' ? seat?.substring(2) : seat?.substring(1)) : seat;
          onShowUnavailableSeatReason(seatToUse, seatsData);
        }

        if (
          (selectedPassenger && selectedPassenger.id === seatPassengerId) ||
          (selectedPassengers?.length && selectedPassengers.some(({ id }) => seatPassengerId === id))
        ) {
          onRemovePassengerSelection(seatPassengerId);
        } else if (seatPassengerId) {
          onSelectNewPassenger(seatPassengerId);
        } else if (selectedPassenger && ![status.NON_EDITABLE, status.UNAVAILABLE, status.BLOCKED].includes(getSeatStatus(seatElement))) {
          onSelectNewSeat(getSeatNumber(seatElement));
        } else if (![status.NON_EDITABLE, status.UNAVAILABLE, status.BLOCKED].includes(getSeatStatus(seatElement))) {
          onSelectNewSeats(getSeatNumber(seatElement), availableSeats);
        }
      }),
    [
      selectedPassenger,
      selectedPassengers,
      onRemovePassengerSelection,
      onSelectNewPassenger,
      onSelectNewSeat,
      onSelectNewSeats,
      availableSeats,
      onShowUnavailableSeatReason,
      seatsData
    ]
  );

  const setStyles = useCallback(
    (seatMapSvg) => {
      const styleId = 'override-styles';
      const existingOverrides = seatMapSvg.querySelector(`#${styleId}`);

      if (existingOverrides) {
        return;
      }

      const fontImport = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      fontImport.innerHTML = fonts.imports;

      const overrides = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      overrides.setAttribute('id', styleId);
      overrides.innerHTML = isERTrain ? svgStyleER : svgStyle;

      const existingStyle = seatMapSvg.querySelector('style');
      existingStyle.after(fontImport);
      existingStyle.after(overrides);
    },
    [isERTrain]
  );

  const setAvailableSeats = useCallback(
    (seatMapSvg) => {
      availableSeats.forEach(({ number }) => {
        const seatNumber = number.padStart(3, '0');
        const seat = getSeatElement(seatMapSvg, seatNumber);

        if (seat) {
          seat.removeAttribute(STATUS_ATTRIBUTE);
        }
      });
    },
    [availableSeats]
  );

  const setPrioritySeats = useCallback(
    (seatMapSvg) => {
      prioritySeats.forEach(({ number }) => {
        const seatNumber = number.padStart(3, '0');
        const seat = getSeatElement(seatMapSvg, seatNumber);

        if (!seat) {
          return;
        }

        const alreadyShowPriority = seat.querySelector('.c-seat__priority');

        if (!alreadyShowPriority) {
          const { x, y, height } = seat.getBBox();

          const seatText = seat.querySelector('.c-seat__text');
          const shouldRotateText = String(seatText.getAttribute('transform')).includes('rotate');

          const priorityText = document.createElementNS('http://www.w3.org/2000/svg', 'text');

          const statusPosition = {};

          if (shouldRotateText) {
            statusPosition.rotate = 90;
            statusPosition.x = x + height - 12;
            statusPosition.y = y + 2;
          } else {
            statusPosition.rotate = 0;
            statusPosition.x = x + 2;
            statusPosition.y = y + 12;
          }

          priorityText.setAttribute('class', 'c-seat__priority');
          priorityText.setAttribute('transform', `translate(${statusPosition.x}, ${statusPosition.y}) rotate(${statusPosition.rotate})`);
          priorityText.setAttribute('font-size', '12');
          priorityText.setAttribute('font-weight', '600');
          priorityText.setAttribute('font-family', fonts.fontFamily);
          priorityText.setAttribute('fill', 'currentcolor');

          priorityText.textContent = 'P';

          seat.appendChild(priorityText);
        }
      });
    },
    [prioritySeats]
  );

  const setSeatsAsUnavailable = useCallback((seatMapSvg) => {
    seatMapSvg.querySelectorAll('.c-seat').forEach((seat) => {
      // Set to unavailable by default
      seat.setAttribute(STATUS_ATTRIBUTE, status.UNAVAILABLE);
    });
  }, []);

  const setSeatsAsBlocked = useCallback(
    (seatMapSvg) => {
      const initiallyAllocatedSeatsOnCurrentCoach = originalSeats.filter((originalSeat) => originalSeat.coach === currentCoach);

      initiallyAllocatedSeatsOnCurrentCoach.forEach((originalSeat) => {
        if (seats.some((currentSeat) => Number(currentSeat.number) !== Number(originalSeat.number))) {
          const seatNumber = originalSeat.number.padStart(3, '0');
          const seatElement = getSeatElement(seatMapSvg, seatNumber);
          setSeatAsBlocked(seatElement);
        }
      });
    },
    [originalSeats, currentCoach, seats]
  );

  const highlightSelectedSeats = useCallback(
    (seatMapSvg) => {
      if (!seatMapSvg) {
        return;
      }

      seats.forEach((seat) => {
        const sameCoach = currentCoach === seat.coach;
        if (!sameCoach) {
          return;
        }

        const seatNumber = seat.number.padStart(3, '0');
        const seatElement = getSeatElement(seatMapSvg, seatNumber);

        if (seat.editable) {
          if (
            (selectedPassenger && selectedPassenger.id === seat.passenger.id) ||
            (selectedPassengers?.length && selectedPassengers.some(({ id }) => id === seat.passenger.id))
          ) {
            setSeatAsSelected(seatElement, seat.passenger.id);
          } else {
            setSeatAsAssigned(seatElement, seat.passenger.id);
          }
        } else {
          setSeatAsNonEditable(seatElement, seat.passenger.id);
        }
      });
    },
    [seats, currentCoach, selectedPassenger, selectedPassengers]
  );

  const setupSeatMap = useCallback(
    (seatMapSvg) => {
      onClickRef.current = createOnClickHandler(seatMapSvg);

      setStyles(seatMapSvg);
      setSeatsAsUnavailable(seatMapSvg);
      setPrioritySeats(seatMapSvg);
      setAvailableSeats(seatMapSvg);
      setSeatsAsBlocked(seatMapSvg);
      highlightSelectedSeats(seatMapSvg);
    },
    [createOnClickHandler, setStyles, setSeatsAsUnavailable, setPrioritySeats, setAvailableSeats, setSeatsAsBlocked, highlightSelectedSeats]
  );

  useEffect(() => {
    const loadSeatingMap = () => {
      const seatMapContainer = getSeatMapContainer(document, '#seat-map-container');
      onSeatMapContainerLoad(seatMapContainer, (seatMap) => {
        if (onClickRef.current) {
          seatMap.removeEventListener('click', onClickRef.current);
        }
        resetSeatMap(seatMap);
        setupSeatMap(seatMap);
      });
    };

    loadSeatingMap();

    return () => {
      const seatMapContainer = getSeatMapContainer(document, '#seat-map-container');
      if (seatMapContainer) {
        const seatMapSvg = seatMapContainer.contentDocument?.querySelector('svg');
        if (seatMapSvg && onClickRef.current) {
          seatMapSvg.removeEventListener('click', onClickRef.current);
          onClickRef.current = null;
        }
      }
    };
  }, [seats, currentCoach, selectedPassenger, selectedPassengers, setupSeatMap]);

  return (
    <StyledSvgObject className={className} id="seat-map-container" key={templateName} data={url} type="image/svg+xml">
      <SeatMapErrorMarkUp />
    </StyledSvgObject>
  );
};

SeatMapSvg.propTypes = {
  templateName: PropTypes.string,
  url: PropTypes.string,
  seats: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.string,
      coach: PropTypes.string
    })
  ),
  className: PropTypes.string,
  availableSeats: seatsShape.isRequired,
  prioritySeats: PropTypes.any,
  originalSeats: seatsShape.isRequired,
  currentCoach: PropTypes.string,
  selectedPassenger: passengerShape,
  selectedPassengers: PropTypes.arrayOf(passengerShape),
  onSelectNewPassenger: PropTypes.func.isRequired,
  onSelectNewSeat: PropTypes.func.isRequired,
  onSelectNewSeats: PropTypes.func.isRequired,
  onRemovePassengerSelection: PropTypes.func.isRequired,
  isERTrain: PropTypes.bool.isRequired,
  onShowUnavailableSeatReason: PropTypes.func,
  seatsData: PropTypes.arrayOf(
    PropTypes.shape({
      coach: PropTypes.string,
      seat: PropTypes.string
    })
  )
};

export default SeatMapSvg;
